import React, { type ComponentType } from 'react';

import { type IntlShape } from 'react-intl-next';
import Loadable from 'react-loadable';

import { DefaultExtensionProvider, type ExtensionParams } from '@atlaskit/editor-common/extensions';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { token } from '@atlaskit/tokens';
import { DEFAULT_GRAY_COLOR } from '@atlassian/color-picker';
import {
	Alignment,
	CornerStyle,
	type CustomSitesExtensionProviderProps,
	type ExtensionMode,
	getExtensionManifest,
	Orientation,
	type ProductDependenciesProviderProps,
} from '@atlassian/company-hub-common';
import { SIZE_VALUES } from '@atlassian/company-hub-common/config-panel';

import {
	JSM_HELP_EXTENSION_KEY,
	JSM_HELP_EXTENSION_TYPE,
	JSM_HELP_EXTENSION_VERSION_1,
	SourceType,
} from '../../constants';
import { HelpCenterSortMode, type JSMHelpParameters } from '../../types';

import { messages } from './messages';

const JSMHelpExtension = Loadable({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "@atlaskit-internal_JSMHelpExtension" */ '../jsm-help-extension'
			)
		).JSMHelpExtension,
	loading: () => <UFOLoadHold name="jsm-help-extension-loader"></UFOLoadHold>,
});

export const getInitialJSMHelpParameters = (intl: IntlShape): JSMHelpParameters => ({
	heading: intl.formatMessage(messages.jsmHelpMacroTitle),
	headingColor: token('color.text'),
	backgroundColor: token('elevation.surface'),
	textColor: token('color.text'),
	cornerStyle: CornerStyle.ROUNDED,
	version: JSM_HELP_EXTENSION_VERSION_1,
	borderColor: DEFAULT_GRAY_COLOR,
	alignment: Alignment.CENTER,
	size: SIZE_VALUES.SMALL,
	sourceType: SourceType.MANUAL,
	orientation: Orientation.STACKED,
	productEntitySortConfig: {
		sortMode: HelpCenterSortMode.PARENT,
		additionalConfig: { manualSortOrder: [] },
	},
	productEntitiesFilter: [],
	entityData: {},
});

export type JSMHelpExtensionProviderWrapperProps = {
	contentId: string;
	mode: ExtensionMode;
	propsProvider: ComponentType<ProductDependenciesProviderProps>;
};

export const JSMHelpExtensionProvider = async ({
	contentId,
	propsProvider,
	mode,
}: JSMHelpExtensionProviderWrapperProps) => {
	return async ({ editorActions, openConfigPanel, intl }: CustomSitesExtensionProviderProps) => {
		const title = intl.formatMessage(messages.jsmHelpMacroTitle);
		const description = intl.formatMessage(messages.jsmHelpMacroDescription);
		const type = JSM_HELP_EXTENSION_TYPE;
		const render = async () => {
			return ({ node }: { node: ExtensionParams<JSMHelpParameters> }) => (
				<JSMHelpExtension
					contentId={contentId}
					mode={mode}
					propsProvider={propsProvider}
					extensionNode={node}
				/>
			);
		};
		const icons = {
			'48': () =>
				import(
					/* webpackChunkName: "@atlaskit-internal_JSMHelpExtensionIcon" */
					'../assets/jsm-help-extension-icon'
				).then((mod) => mod.JSMHelpExtensionIcon),
		};

		const parameters = getInitialJSMHelpParameters(intl);

		const update = async () => {
			const selectedNode = editorActions?.getSelectedNode()?.toJSON();
			const selectedLocalId = selectedNode?.attrs?.localId;

			if (!!selectedLocalId && openConfigPanel) {
				openConfigPanel(selectedLocalId);
			}
		};

		return new DefaultExtensionProvider([
			getExtensionManifest<JSMHelpParameters>({
				key: JSM_HELP_EXTENSION_KEY,
				icons,
				parameters,
				render,
				update,
				title,
				type,
				description,
				source: undefined,
			}),
		]);
	};
};
